import dayjs from 'dayjs';

import {
  AttendanceStatus,
  CertificateResultStatus,
  OpenClassCertificateType,
  OpenClassRegistration,
  OpenClassSession,
} from '../types/OpenClass';
import { attendedAllSessions, failedAnySession } from './attendance';
import { certificateIssued } from './certificateEligibility';
import { SessionAttendeeStatus } from './sessionAttendees';

export type ClassAttendeeSession = Pick<
  OpenClassSession,
  'endAt' | 'startAt'
> & {
  attendanceStatus: AttendanceStatus;
  result: CertificateResultStatus;
  sessionId: OpenClassSession['id'];
};

export type ClassAttendee = {
  attendedAllSessions: boolean;
  failedSession: boolean;
  certificateIssued: boolean;
  certificateResultStatus: CertificateResultStatus;
  besaccCertificateFileName?: string | null;
  certificateSentAt?: string | null;
  certificateSentTo?: string | null;
  certificateType: OpenClassCertificateType;
  clientCompanyId?: number;
  clientCompanyName?: string;
  email?: string | null;
  name: string;
  registrationId: OpenClassRegistration['id'];
  sessions: ClassAttendeeSession[];
};

export type SessionCountFilter = 'all' | 'multiple' | 'single';

// One row per registration rather than per attendance, since a registration covers every
// session its attendee booked.
export const classAttendees = (
  registrations: OpenClassRegistration[],
  sessions: OpenClassSession[],
): ClassAttendee[] => {
  const canceledSessionIds = new Set(
    sessions.filter(({ status }) => status === 'canceled').map(({ id }) => id),
  );
  const sessionsById = new Map(
    sessions
      .filter(({ id }) => !canceledSessionIds.has(id))
      .map((session) => [session.id, session]),
  );

  return registrations.map((registration) => {
    const activeAttendances = (registration.sessionAttendances ?? []).filter(
      ({ sessionId }) => !canceledSessionIds.has(sessionId),
    );

    return {
      attendedAllSessions: attendedAllSessions(activeAttendances),
      failedSession: failedAnySession(activeAttendances),
      certificateIssued: certificateIssued(registration),
      certificateResultStatus: registration.certificateResultStatus,
      besaccCertificateFileName: registration.besaccCertificateFileName,
      certificateSentAt: registration.certificateSentAt,
      certificateSentTo: registration.certificateSentTo,
      certificateType: registration.certificateType,
      clientCompanyId: registration.attendee.clientCompany?.id,
      clientCompanyName: registration.attendee.clientCompany?.name,
      email: registration.attendee.email,
      name: registration.attendee.name,
      registrationId: registration.id,
      sessions: activeAttendances
        .flatMap((attendance) => {
          const session = sessionsById.get(attendance.sessionId);
          return session
            ? [
                {
                  attendanceStatus: attendance.attendanceStatus,
                  result: attendance.result,
                  sessionId: session.id,
                  startAt: session.startAt,
                  endAt: session.endAt,
                },
              ]
            : [];
        })
        .sort(
          (left, right) =>
            dayjs(left.startAt).valueOf() - dayjs(right.startAt).valueOf(),
        ),
    };
  });
};

const recordedAttendance = (
  sessions: Pick<ClassAttendeeSession, 'attendanceStatus'>[],
): Set<AttendanceStatus> =>
  new Set(
    sessions
      .map(({ attendanceStatus }) => attendanceStatus)
      .filter((attendanceStatus) => attendanceStatus !== 'not_recorded'),
  );

// Attendance is recorded per session, so a class wide row can only say how they add up.
// A session nobody has marked yet says nothing, it doesn't make the row partial.
export const classAttendanceSummary = (
  sessions: Pick<ClassAttendeeSession, 'attendanceStatus'>[],
): 'notPresent' | 'notRecorded' | 'partlyPresent' | 'present' => {
  const recorded = recordedAttendance(sessions);
  if (recorded.size === 0) return 'notRecorded';
  if (recorded.size > 1) return 'partlyPresent';
  return recorded.has('present') ? 'present' : 'notPresent';
};

// Same precedence as a single session, an absence outranks a result and a result outranks
// bare attendance.
export const classAttendeeStatus = (
  attendee: Pick<ClassAttendee, 'certificateResultStatus' | 'sessions'>,
): SessionAttendeeStatus => {
  const attendance = classAttendanceSummary(attendee.sessions);
  if (attendance === 'notPresent') return 'notPresent';
  if (attendee.certificateResultStatus === 'passed') return 'passed';
  if (attendee.certificateResultStatus === 'failed') return 'failed';
  return attendance;
};

// Writing one attendance across every session would bury a deliberate difference. An
// unmarked session isn't one, there is nothing there to lose.
export const hasDivergentAttendance = (
  attendee: Pick<ClassAttendee, 'sessions'>,
): boolean => recordedAttendance(attendee.sessions).size > 1;

export const filterBySessionCount = (
  attendees: ClassAttendee[],
  filter: SessionCountFilter,
): ClassAttendee[] =>
  filter === 'all'
    ? attendees
    : attendees.filter(({ sessions }) =>
        filter === 'multiple' ? sessions.length > 1 : sessions.length <= 1,
      );

export type ClassAttendeeCsvRow = {
  name: string;
  email: string;
  clientCompany: string;
  certificateType: string;
  result: CertificateResultStatus;
  sessionCount: number;
  sessions: string;
};

export const classAttendeeCsvColumns: (keyof ClassAttendeeCsvRow)[] = [
  'name',
  'email',
  'clientCompany',
  'certificateType',
  'result',
  'sessionCount',
  'sessions',
];

export const classAttendeeCsvRows = (
  attendees: ClassAttendee[],
  privateParticipantLabel: string,
  certificateTypeLabel: (type: OpenClassCertificateType) => string,
): ClassAttendeeCsvRow[] =>
  attendees.map((attendee) => ({
    name: attendee.name,
    email: attendee.email ?? '',
    clientCompany: attendee.clientCompanyName || privateParticipantLabel,
    certificateType: certificateTypeLabel(attendee.certificateType),
    result: attendee.certificateResultStatus,
    sessionCount: attendee.sessions.length,
    sessions: attendee.sessions
      .map(({ startAt }) => dayjs(startAt).format('YYYY-MM-DD HH:mm'))
      .join('; '),
  }));
